import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/lib/api/axios';

import type {
  PamActionSuggestionsResponse,
  PamAiEvidenceSearchResult,
  PamChecklistItemUpdate,
  PamChecklistReviewState,
  PamCostedAction,
  PamCostedActionCreate,
  PamCostedActionCreatedResponse,
  PamQuestionActivityResponse,
  PamQuestionAnswerCreate,
  PamQuestionAnswerScopeUpdate,
  PamQuestionAnswerUpdate,
  PamQuestionCommentCreate,
  PamQuestionCommentUpdate,
  PamQuestionContentFeedbackCreate,
  PamQuestionContentFeedbackResponse,
  PamQuestionDeadlineUpdate,
  PamQuestionEvidenceCreate,
  PamQuestionFinalAnswerUpdate,
  PamQuestionOtherEvidenceCreate,
  PamQuestionOtherEvidenceUpdate,
  PamQuestionOverviewResponse,
  PamSaqComment
} from './types';

const BASE_PATH = '/pam';

// AI evidence search runs a multi-step agent (LLM + Document Hub retrieval) and
// can take several minutes on large hubs. Keep below the Azure Front Door limit (~240s).
const AI_EVIDENCE_SEARCH_TIMEOUT_MS = 5 * 60 * 1000;

// Questions are leaf-addressed: the question ULID alone resolves the full
// SAQ/submission chain on the backend, so no parent ids appear in the path.
function questionPath(questionId: string, suffix = ''): string {
  return `${BASE_PATH}/questions/${questionId}${suffix}`;
}

export async function getPamQuestionOverview(questionId: string): Promise<PamQuestionOverviewResponse> {
  const response = await apiGet<PamQuestionOverviewResponse>(`${BASE_PATH}/questions/${questionId}`);
  return response.data;
}

export async function updateQuestionFinalAnswer(
  questionId: string,
  payload: PamQuestionFinalAnswerUpdate
): Promise<void> {
  await apiPatch(questionPath(questionId, '/final-answer'), payload);
}

export async function setQuestionChecklistItem(
  questionId: string,
  checklistItemId: string,
  payload: PamChecklistItemUpdate
): Promise<void> {
  await apiPatch(questionPath(questionId, `/checklist/${checklistItemId}`), payload);
}

export async function startChecklistAiReview(questionId: string): Promise<PamChecklistReviewState> {
  const response = await apiPost<PamChecklistReviewState>(
    questionPath(questionId, '/checklist/ai-review'),
    {},
    { meta: { globalError: false, context: 'pam-question-ai-review' } }
  );
  return response.data;
}

export async function updateQuestionAnswerScope(
  questionId: string,
  payload: PamQuestionAnswerScopeUpdate
): Promise<void> {
  await apiPatch(questionPath(questionId, '/answer-scope'), payload);
}

export async function addQuestionAnswer(questionId: string, payload: PamQuestionAnswerCreate): Promise<void> {
  await apiPost(questionPath(questionId, '/answers'), payload, {
    meta: { globalError: false, context: 'pam-question-add-answer' }
  });
}

export async function updateQuestionAnswer(
  questionId: string,
  answerId: string,
  payload: PamQuestionAnswerUpdate
): Promise<void> {
  await apiPatch(questionPath(questionId, `/answers/${answerId}`), payload);
}

// Registers an already-uploaded Document Hub document as question evidence.
// `site_ids` is `[null]` for a trust (all-sites) upload, or concrete site ids.
export async function registerQuestionEvidence(questionId: string, payload: PamQuestionEvidenceCreate): Promise<void> {
  await apiPost(questionPath(questionId, '/evidence'), payload, {
    meta: { globalError: false, context: 'pam-question-evidence-upload' }
  });
}

export async function addOtherEvidence(questionId: string, payload: PamQuestionOtherEvidenceCreate): Promise<void> {
  await apiPost(questionPath(questionId, '/evidence/other'), payload);
}

export async function updateOtherEvidence(
  questionId: string,
  evidenceId: string,
  payload: PamQuestionOtherEvidenceUpdate
): Promise<void> {
  await apiPatch(questionPath(questionId, `/evidence/${evidenceId}/other`), payload);
}

export async function setEvidenceSites(questionId: string, documentId: string, siteIds: string[]): Promise<void> {
  await apiPut(questionPath(questionId, `/evidence/document/${documentId}/sites`), {
    site_ids: siteIds
  });
}

export async function setOtherEvidenceSites(questionId: string, evidenceId: string, siteIds: string[]): Promise<void> {
  await apiPut(questionPath(questionId, `/evidence/other/${evidenceId}/sites`), {
    site_ids: siteIds
  });
}

export async function removeQuestionEvidence(questionId: string, evidenceId: string): Promise<void> {
  await apiDelete(questionPath(questionId, `/evidence/${evidenceId}`));
}

export async function updateQuestionDeadline(questionId: string, payload: PamQuestionDeadlineUpdate): Promise<void> {
  await apiPatch(questionPath(questionId, '/deadline'), payload);
}

export async function postQuestionComment(
  questionId: string,
  payload: PamQuestionCommentCreate
): Promise<PamSaqComment> {
  const response = await apiPost<PamSaqComment>(questionPath(questionId, '/comments'), payload);
  return response.data;
}

export async function editQuestionComment(
  questionId: string,
  commentId: string,
  payload: PamQuestionCommentUpdate
): Promise<PamSaqComment> {
  const response = await apiPatch<PamSaqComment>(questionPath(questionId, `/comments/${commentId}`), payload);
  return response.data;
}

export async function removeQuestionComment(questionId: string, commentId: string): Promise<void> {
  await apiDelete(questionPath(questionId, `/comments/${commentId}`));
}

export async function createQuestionCostedAction(
  questionId: string,
  payload: PamCostedActionCreate
): Promise<PamCostedActionCreatedResponse> {
  const response = await apiPost<PamCostedActionCreatedResponse>(questionPath(questionId, '/costed-actions'), payload, {
    meta: { globalError: false, context: 'pam-question-add-costed-action' }
  });
  return response.data;
}

// Runs the LLM synchronously and returns suggested remediation actions. Nothing
// is persisted server-side; approving a suggestion creates a costed action via
// the costed-actions path. Errors are surfaced locally (no global toast):
// 404 = feature unavailable, 503 = AI failure (retryable).
export async function suggestQuestionActions(questionId: string): Promise<PamActionSuggestionsResponse> {
  const response = await apiPost<PamActionSuggestionsResponse>(
    questionPath(questionId, '/action-suggestions'),
    {},
    { meta: { globalError: false, context: 'pam-question-action-suggestions' } }
  );
  return response.data;
}

export async function submitQuestionContentFeedback(
  questionId: string,
  payload: PamQuestionContentFeedbackCreate
): Promise<PamQuestionContentFeedbackResponse> {
  const response = await apiPost<PamQuestionContentFeedbackResponse>(
    questionPath(questionId, '/content-feedback'),
    payload,
    { meta: { globalError: false, context: 'pam-question-content-feedback' } }
  );
  return response.data;
}

export async function getQuestionCostedActions(questionId: string): Promise<PamCostedAction[]> {
  const response = await apiGet<PamCostedAction[]>(questionPath(questionId, '/costed-actions'));
  return response.data;
}

export async function getQuestionActivity(
  questionId: string,
  cursor?: string | null
): Promise<PamQuestionActivityResponse> {
  const params = new URLSearchParams({ limit: '50' });
  if (cursor) params.set('cursor', cursor);
  const response = await apiGet<PamQuestionActivityResponse>(questionPath(questionId, `/activity?${params}`));
  return response.data;
}

export async function searchQuestionEvidenceWithAI(questionId: string): Promise<PamAiEvidenceSearchResult | null> {
  const response = await apiPost<PamAiEvidenceSearchResult | null>(
    questionPath(questionId, '/evidence/ai-search'),
    {},
    {
      timeout: AI_EVIDENCE_SEARCH_TIMEOUT_MS,
      meta: { globalError: false, context: 'pam-question-ai-evidence-search' }
    }
  );
  return response.data;
}
